import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { roomsData } from '../data/roomsData';
import { Calendar, Users, CreditCard, Mail, Phone, User, Check, X } from 'lucide-react';
import { useReservation } from '../context/ReservationContext';
import HeroImage from '../Resources/camera4.jpeg';
const BookingPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialRoomId = searchParams.get('room') || '';
  const { makeReservation, exportToExcel } = useReservation();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    roomId: initialRoomId,
    checkIn: '',
    checkOut: '',
    guests: 1,
    specialRequests: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPhonePopup, setShowPhonePopup] = useState(false);

  const selectedRoom = roomsData.find(room => room.id === formData.roomId);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowPhonePopup(true);
  };

  const handleConfirmBooking = async () => {
    setSubmitting(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Save reservation
      makeReservation({
        ...formData,
        id: Date.now().toString(),
        roomName: selectedRoom?.name || '',
        created: new Date().toISOString(),
        totalPrice: calculateTotalPrice(),
        status: 'confirmed',
      });

      // Export updated reservations to Excel
      exportToExcel();

      setSuccess(true);
      setShowPhonePopup(false);
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        roomId: '',
        checkIn: '',
        checkOut: '',
        guests: 1,
        specialRequests: '',
      });

    } catch (error) {
      console.error('Error making reservation:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const calculateNights = () => {
    if (!formData.checkIn || !formData.checkOut) return 0;

    const checkIn = new Date(formData.checkIn);
    const checkOut = new Date(formData.checkOut);

    const diffTime = checkOut.getTime() - checkIn.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays > 0 ? diffDays : 0;
  };

  const calculateTotalPrice = () => {
    if (!selectedRoom) return 0;

    const nights = calculateNights();
    return selectedRoom.price * nights;
  };

  useEffect(() => {
    // Set minimum dates for check-in/out
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    const minCheckInDate = tomorrow.toISOString().split('T')[0];

    const dayAfterTomorrow = new Date();
    dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);

    const minCheckOutDate = dayAfterTomorrow.toISOString().split('T')[0];

    // Set default check-in/out dates
    if (!formData.checkIn) {
      setFormData(prev => ({ ...prev, checkIn: minCheckInDate }));
    }

    if (!formData.checkOut) {
      setFormData(prev => ({ ...prev, checkOut: minCheckOutDate }));
    }
  }, []);

  return (
      <div>
        {/* Hero Section */}
        <section className="h-[30vh] relative flex items-center justify-center">
          <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url(${HeroImage})`,
              }}
          >
            <div className="absolute inset-0 bg-black opacity-40"></div>
          </div>

          <div className="container mx-auto px-4 md:px-6 relative z-10 text-center">
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4">
              Rezerva camera
            </h1>
            <p className="text-xl text-white mb-0 max-w-2xl mx-auto">
              Inca cativa pasi si ai sejurul asigurat
            </p>
          </div>
        </section>

        {/* Booking Form */}
        <section className="py-16 bg-sage-50">
          <div className="container mx-auto px-4 md:px-6">
            {success ? (
                <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md p-8 text-center">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check className="h-10 w-10 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-serif font-bold text-gray-800 mb-4">Booking Successful!</h2>
                  <p className="text-gray-600 mb-6">
                    Thank you for your reservation. We have sent a confirmation email with all the details.
                    We look forward to welcoming you to Casa Leahu!
                  </p>
                  <button
                      onClick={() => setSuccess(false)}
                      className="bg-sage-700 hover:bg-sage-800 text-white px-6 py-3 rounded-md transition-colors shadow-md"
                  >
                    Make Another Booking
                  </button>
                </div>
            ) : (
                <div className="flex flex-col lg:flex-row gap-8">
                  {/* Booking Form */}
                  <div className="w-full lg:w-2/3">
                    <div className="bg-white rounded-lg shadow-md p-8">
                      <h2 className="text-2xl font-serif font-bold text-gray-800 mb-6">Detalii rezervare</h2>

                      <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                          <div>
                            <label className="block text-gray-700 font-medium mb-2" htmlFor="roomId">
                              Selectati camera
                            </label>
                            <div className="relative">
                              <select
                                  id="roomId"
                                  name="roomId"
                                  value={formData.roomId}
                                  onChange={handleChange}
                                  required
                                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sage-500 appearance-none"
                              >
                                <option value="">Selectati camera</option>
                                {roomsData.map(room => (
                                    <option key={room.id} value={room.id}>
                                      {room.name} - RON{room.price}/night
                                    </option>
                                ))}
                              </select>
                              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </div>
                            </div>
                          </div>

                          <div>
                            <label className="block text-gray-700 font-medium mb-2" htmlFor="guests">
                              Numar de oaspeti
                            </label>
                            <div className="relative">
                              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                                <Users className="h-5 w-5" />
                              </div>
                              <input
                                  type="number"
                                  id="guests"
                                  name="guests"
                                  min="1"
                                  max={selectedRoom?.capacity || 4}
                                  value={formData.guests}
                                  onChange={handleChange}
                                  required
                                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sage-500"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-gray-700 font-medium mb-2" htmlFor="checkIn">
                              Check-in Date
                            </label>
                            <div className="relative">
                              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                                <Calendar className="h-5 w-5" />
                              </div>
                              <input
                                  type="date"
                                  id="checkIn"
                                  name="checkIn"
                                  value={formData.checkIn}
                                  onChange={handleChange}
                                  required
                                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sage-500"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-gray-700 font-medium mb-2" htmlFor="checkOut">
                              Check-out Date
                            </label>
                            <div className="relative">
                              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                                <Calendar className="h-5 w-5" />
                              </div>
                              <input
                                  type="date"
                                  id="checkOut"
                                  name="checkOut"
                                  min={formData.checkIn}
                                  value={formData.checkOut}
                                  onChange={handleChange}
                                  required
                                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sage-500"
                              />
                            </div>
                          </div>
                        </div>

                        <h3 className="text-xl font-serif font-bold text-gray-800 mb-4">Personal Information</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                          <div>
                            <label className="block text-gray-700 font-medium mb-2" htmlFor="firstName">
                              Prenume
                            </label>
                            <div className="relative">
                              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                                <User className="h-5 w-5" />
                              </div>
                              <input
                                  type="text"
                                  id="firstName"
                                  name="firstName"
                                  value={formData.firstName}
                                  onChange={handleChange}
                                  required
                                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sage-500"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-gray-700 font-medium mb-2" htmlFor="lastName">
                              Nume
                            </label>
                            <div className="relative">
                              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                                <User className="h-5 w-5" />
                              </div>
                              <input
                                  type="text"
                                  id="lastName"
                                  name="lastName"
                                  value={formData.lastName}
                                  onChange={handleChange}
                                  required
                                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sage-500"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-gray-700 font-medium mb-2" htmlFor="email">
                              Email
                            </label>
                            <div className="relative">
                              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                                <Mail className="h-5 w-5" />
                              </div>
                              <input
                                  type="email"
                                  id="email"
                                  name="email"
                                  value={formData.email}
                                  onChange={handleChange}
                                  required
                                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sage-500"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-gray-700 font-medium mb-2" htmlFor="phone">
                              Telefon
                            </label>
                            <div className="relative">
                              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                                <Phone className="h-5 w-5" />
                              </div>
                              <input
                                  type="tel"
                                  id="phone"
                                  name="phone"
                                  value={formData.phone}
                                  onChange={handleChange}
                                  required
                                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sage-500"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="mb-6">
                          <label className="block text-gray-700 font-medium mb-2" htmlFor="specialRequests">
                            Cereri speciale
                          </label>
                          <textarea
                              id="specialRequests"
                              name="specialRequests"
                              value={formData.specialRequests}
                              onChange={handleChange}
                              rows={4}
                              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sage-500"
                              placeholder="Any special requests or preferences..."
                          ></textarea>
                        </div>

                        <div className="flex items-center mb-6">
                          <input
                              type="checkbox"
                              id="terms"
                              required
                              className="h-4 w-4 text-sage-700 border-gray-300 rounded focus:ring-sage-500"
                          />
                          <label htmlFor="terms" className="ml-2 text-gray-700">
                            I agree to the <a href="#" className="text-sage-700 hover:underline">terms and conditions</a>
                          </label>
                        </div>

                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-sage-700 hover:bg-sage-800 text-white py-3 rounded-md transition-colors text-center block font-medium disabled:bg-gray-400"
                        >
                          {submitting ? 'Processing...' : 'Complete Reservation'}
                        </button>
                      </form>
                    </div>
                  </div>

                  {/* Booking Summary */}
                  <div className="w-full lg:w-1/3">
                    <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
                      <h3 className="text-xl font-serif font-bold text-gray-800 mb-4">Sumar rezervare</h3>

                      {selectedRoom ? (
                          <>
                            <div className="mb-4">
                              <img
                                  src={selectedRoom.images[0]}
                                  alt={selectedRoom.name}
                                  className="w-full h-48 object-cover rounded-md mb-4"
                              />
                              <h4 className="font-bold text-gray-800">{selectedRoom.name}</h4>
                              <p className="text-gray-600 text-sm">RON{selectedRoom.price} per night</p>
                            </div>

                            <div className="border-t border-gray-200 pt-4 mb-4">
                              <div className="flex justify-between mb-2">
                                <span className="text-gray-600">Check-in</span>
                                <span className="font-medium">
                            {formData.checkIn ? new Date(formData.checkIn).toLocaleDateString() : '-'}
                          </span>
                              </div>
                              <div className="flex justify-between mb-2">
                                <span className="text-gray-600">Check-out</span>
                                <span className="font-medium">
                            {formData.checkOut ? new Date(formData.checkOut).toLocaleDateString() : '-'}
                          </span>
                              </div>
                              <div className="flex justify-between mb-2">
                                <span className="text-gray-600">Oaspeti</span>
                                <span className="font-medium">{formData.guests}</span>
                              </div>
                              <div className="flex justify-between mb-2">
                                <span className="text-gray-600">Durata</span>
                                <span className="font-medium">{calculateNights()} nopti</span>
                              </div>
                            </div>

                            <div className="border-t border-gray-200 pt-4 mb-4">
                              <div className="flex justify-between mb-2">
                                <span className="text-gray-600">Pret camera</span>
                                <span className="font-medium">RON{selectedRoom.price} × {calculateNights()}</span>
                              </div>
                              <div className="flex justify-between font-bold text-lg mt-4">
                                <span>Total</span>
                                <span>RON{calculateTotalPrice()}</span>
                              </div>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-md mt-6">
                              <h4 className="font-bold text-gray-800 mb-2 flex items-center">
                                <CreditCard className="h-5 w-5 mr-2 text-sage-700" />
                                Plata
                              </h4>
                              <p className="text-gray-600 text-sm">
                                Plata se poate face cu cardul,carduri de vacanta UP si EDENRED sau cash la locatie.
                              </p>
                            </div>
                          </>
                      ) : (
                          <div className="text-center py-8">
                            <p className="text-gray-500">Va rugăm să selectați o cameră</p>
                          </div>
                      )}
                    </div>
                  </div>
                </div>
            )}
          </div>
        </section>

        {/* Phone Popup */}
        {showPhonePopup && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-gray-800">Verificare disponibilitate</h3>
                  <button
                      onClick={() => setShowPhonePopup(false)}
                      className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
                <p className="text-gray-600 mb-6">
                  Pentru a verifica disponibilitatea camerelor și a finaliza rezervarea, vă rugăm să sunați la numărul:
                </p>
                <div className="bg-sage-50 p-4 rounded-lg text-center mb-6">
                  <a
                      href="tel:0773922165"
                      className="text-2xl font-bold text-sage-700 hover:text-sage-800"
                  >
                    0773 922 165
                  </a>
                </div>
                <div className="flex justify-end gap-4">
                  <button
                      onClick={() => setShowPhonePopup(false)}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Închide
                  </button>
                  <button
                      onClick={handleConfirmBooking}
                      className="px-6 py-2 bg-sage-700 hover:bg-sage-800 text-white rounded-md"
                  >
                    Am sunat
                  </button>
                </div>
              </div>
            </div>
        )}
      </div>
  );
};

export default BookingPage;